import React from 'react';
import { motion } from 'framer-motion';
import { Wrench, ShoppingBag, Palette, Settings } from 'lucide-react';

const Services: React.FC = () => {
  const services = [
    {
      icon: Wrench,
      title: 'Autobody Repairs',
      description: 'From minor dents and scratches to complete collision repairs, we restore your classic Mini to its original glory.',
      features: ['Dent & Scratch Repair', 'Collision Restoration', 'Rust Treatment', 'Panel Replacement'],
      color: 'mini-red'
    },
    {
      icon: Palette,
      title: 'Paint & Bodywork',
      description: 'Expert paint matching and bodywork that brings your Mini back to showroom condition.',
      features: ['Custom Paint Jobs', 'Color Matching', 'Primer & Base Coats', 'Clear Coat Finishing'],
      color: 'mini-green'
    },
    {
      icon: ShoppingBag,
      title: 'Authentic Parts Sales',
      description: 'Wide range of authentic and high-quality aftermarket Mini parts sourced from trusted suppliers.',
      features: ['Original Equipment', 'Aftermarket Quality', 'Vintage Sourcing', 'Custom Orders'],
      color: 'blue-600'
    },
    {
      icon: Settings,
      title: 'Complete Restorations',
      description: 'Full frame-off restorations combining autobody expertise with mechanical knowledge.',
      features: ['Frame-off Restoration', 'Body Shell Restoration', 'Interior Restoration', 'Mechanical Assembly'],
      color: 'purple-600'
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <section id="services" className="section-padding bg-white relative overflow-hidden">
      {/* Background Graphics */}
      <div className="absolute top-20 right-10 opacity-5">
        <svg width="200" height="100" viewBox="0 0 200 100" className="text-mini-green mini-float">
          <path d="M20 70 C15 65, 15 35, 20 30 L40 20 C50 15, 80 10, 100 10 C120 10, 150 15, 160 20 L180 30 C185 35, 185 65, 180 70 L170 80 C170 85, 165 90, 160 90 L150 90 C145 90, 140 85, 140 80 L60 80 C60 85, 55 90, 50 90 L40 90 C35 90, 30 85, 30 80 Z" fill="currentColor" />
          <circle cx="55" cy="85" r="10" />
          <circle cx="145" cy="85" r="10" />
          <path d="M40 30 C50 20, 70 10, 100 10 C130 10, 150 20, 160 30 L150 50 L50 50 Z" fill="currentColor" opacity="0.7" />
        </svg>
      </div>
      
      <div className="container-custom relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center space-x-2 bg-mini-red/10 text-mini-red px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Settings size={16} />
            <span>Our Services</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl section-heading text-gray-900 dark:text-white mb-6">
            Complete Mini Care Solutions
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            From routine maintenance to complete restorations, we offer comprehensive services 
            to keep your classic Mini in perfect condition.
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 xl:grid-cols-4 gap-8"
          variants={staggerChildren}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="card p-6 group"
              variants={fadeInUp}
              whileHover={{ y: -10 }}
            >
              <motion.div
                className={`w-16 h-16 bg-${service.color}/10 rounded-xl flex items-center justify-center mb-6`}
                whileHover={{ scale: 1.18, rotate: 8 }}
                whileTap={{ scale: 0.95, rotate: -8 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              >
                <service.icon size={32} className={`text-${service.color} drop-shadow`} />
              </motion.div>
              
              <h3 className="text-xl font-heading font-bold text-gray-900 dark:text-white mb-4">
                {service.title}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                {service.description}
              </p>
              
              <ul className="space-y-2">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <div className={`w-1.5 h-1.5 bg-${service.color} rounded-full mr-3 flex-shrink-0`} />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <motion.a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`mt-6 w-full py-2 px-4 border-2 border-${service.color} text-${service.color} hover:bg-${service.color} hover:text-white rounded-lg font-medium transition-all duration-300 text-center inline-block`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn More
              </motion.a>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-gradient-to-r from-mini-red to-mini-green rounded-2xl p-8 text-white">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
              Ready to Restore Your Classic Mini?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Get a free consultation and quote for your Mini restoration project.
            </p>
            <motion.a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center bg-white text-mini-red hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Free Quote
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
